import { type AvailablePlansTypes } from '@constants/plans'
import { setCheckoutData } from '@pages/Checkout/helpers/checkoutData'
import { isAvailablePlan } from '@pages/Checkout/helpers/isAvailablePlan'
import { useGetPlanLimits } from '@pages/Checkout/hooks/useGetPlanLimits'
import { useGetPurchaseSummaryInfo } from '@pages/Checkout/hooks/useGetPurchaseSummaryInfo'
import { useCheckoutContext } from '@pages/Checkout/routes/contexts'
import { PAYMENTS } from '@routes/path'
import {
  CustomLink,
  Heading,
  Panel,
  Paragraph,
  Spacer,
  Spreader
} from '@ui-kit'
import { useTranslation } from 'react-i18next'
import { Column, Row } from 'simple-flexbox'

import styles from './PlanVisitsPanel.module.scss'

export const PlanVisitsPanel = ({
  chosenRecordKey
}: {
  chosenRecordKey: AvailablePlansTypes
}) => {
  const { t } = useTranslation()

  const { goToPreviousStep, checkoutDataInContext, setCheckoutDataInContext } =
    useCheckoutContext()

  const { planName } = useGetPurchaseSummaryInfo()

  const handleChangePlan = () => {
    goToPreviousStep(PAYMENTS.CHECKOUT.PLANS)
  }

  const handleChangeVisits = (option: AvailablePlansTypes) => {
    setCheckoutData({
      selectedRecordKey: option,
      currentStep: PAYMENTS.CHECKOUT.CONFIGURE
    })

    setCheckoutDataInContext({
      ...checkoutDataInContext,
      chosenRecordKey: option
    })
  }

  const { visits: selectWithVisits } = useGetPlanLimits({
    recordKey: chosenRecordKey,
    setPlanTypeName: value => handleChangeVisits(value as AvailablePlansTypes)
  })

  if (!chosenRecordKey) return null

  const shouldRenderPlanVisitsPanel = isAvailablePlan(chosenRecordKey)

  if (!shouldRenderPlanVisitsPanel) return null

  return (
    <Panel padding={30} surface={0} border='neutral-2'>
      <Heading level={3} weight={600} data-testid='plan-visits-panel-heading'>
        {t('checkout.configure.plan.visits')}
      </Heading>

      <Spacer space={20} />

      <Row justifyContent='space-between'>
        <Column justifyContent='space-between'>
          <Paragraph color='neutral-6'>
            {t('checkout.configure.plan')}
          </Paragraph>

          <Row alignItems='center'>
            <Paragraph size={16}>{planName}</Paragraph>

            <Spreader spread={15} />

            <CustomLink
              size={12}
              onClick={handleChangePlan}
              data-testid='change-plan-button'
            >
              {t('checkout.configure.change.plan')}
            </CustomLink>
          </Row>
        </Column>

        <Column className={styles['plan-visits-panel__select']}>
          <Paragraph color='neutral-6'>
            {t('checkout.configure.visits.per.month')}
          </Paragraph>

          {selectWithVisits}
        </Column>
      </Row>
    </Panel>
  )
}
